import {useCompanyQuery} from "@/api/companyApi";
import {useComposedCampaignState} from "@/store/campaignForm";
import useCampaignManagementStore from "@/store/campaignManagement";
import useUserStore from "@/store/user";
import {useDebugValue, useMemo} from "react";
import {useLocation, useParams} from "react-router";

const usePackageLimits = ({
  ownProfile,
  isEnabled = true,
}: {
  ownProfile?: boolean;
  isEnabled?: boolean;
} = {}) => {
  useDebugValue("usePackageLimits");
  const location = useLocation();
  const {agencyId, companyId: companyIdFromUrl} = useParams<{
    agencyId: string;
    companyId: string;
  }>();
  const managedCompanyId = useCampaignManagementStore(state => state.campaign?.company_profile?.id);
  const campaignFormCompanyId = useComposedCampaignState(state => state.misc.companyId);
  const companyId = managedCompanyId ?? campaignFormCompanyId ?? Number(companyIdFromUrl);
  const loggedInUser = useUserStore(state => state.user);
  const isViewingOwnDashboard = useMemo(
    () =>
      (/^\/company\/\d+\/dashboard\/$/.test(location.pathname) &&
        !!loggedInUser.sub_brands?.find(subBrand => subBrand?.company?.id === Number(companyId))) ||
      (/^\/agency\/\d+\/dashboard\/$/.test(location.pathname) &&
        !!loggedInUser.agencies?.find(agency => agency?.agency?.id === Number(agencyId))),
    [location.pathname, companyId, agencyId, loggedInUser],
  );
  const isOwnProfile = useMemo(
    () =>
      ownProfile ||
      !!loggedInUser.sub_brands?.find(subBrand => subBrand?.company?.id === companyId) ||
      isViewingOwnDashboard,
    [loggedInUser, ownProfile, companyId, isViewingOwnDashboard],
  );
  const {data: fetchedCompany, isLoading} = useCompanyQuery({
    companyId: companyId,
    queryOptions: {
      enabled: !isOwnProfile && loggedInUser?.user_type === "admin" && !!companyId && isEnabled,
    },
  });

  const companySubscription = useMemo(
    () => (isOwnProfile ? loggedInUser.package_subscription : fetchedCompany?.package_subscription),
    [fetchedCompany, loggedInUser, isOwnProfile, isLoading],
  );
  const companyContentBundles = useMemo(
    () => (isOwnProfile ? loggedInUser.buyouts : fetchedCompany?.buyouts),
    [fetchedCompany, loggedInUser, isOwnProfile, isLoading],
  );

  const totalBuyouts = useMemo(
    () =>
      (companySubscription?.buyout ?? 0) +
      (companyContentBundles?.reduce((acc, bundle) => acc + bundle.buyout, 0) ?? 0),
    [companySubscription, companyContentBundles],
  );
  const remainingBuyouts = useMemo(
    () =>
      totalBuyouts -
      (companySubscription?.buyout_used ?? 0) -
      (companyContentBundles?.reduce((acc, bundle) => acc + bundle.buyout_used, 0) ?? 0),
    [companySubscription, companyContentBundles, totalBuyouts],
  );

  const returnValue = useMemo(() => {
    return {
      isLoading,
      isOwnProfile,
      hasSubscription: !!companySubscription,
      inviteLimit: companySubscription?.invitations_per_campaign ?? 1000000,
      influencerLimit: companySubscription?.influencers_per_campaign ?? 1000000,
      validUntil: companySubscription?.valid_until,
      containsMultipleCampaigns: companySubscription?.multiple_active_campaigns ?? false,
      parallelCampaignLimit: companySubscription?.number_of_multiple_active_campaigns,
      totalBuyouts,
      remainingBuyouts,
      packageIncludedBuyouts: companySubscription?.buyout ?? 0,
      includesInsights: companySubscription?.influencer_level_insights ?? false,
      packageCategory: companySubscription?.package_category,
      currency: companySubscription?.currency ?? fetchedCompany?.currency ?? loggedInUser.currency,
      hasProCreatorMode: companySubscription?.pro_creator_mode,
    };
  }, [companySubscription, isLoading, remainingBuyouts, totalBuyouts]);
  return returnValue;
};

export default usePackageLimits;
